import { useEffect, useMemo, useState } from 'react';

/**
 * ScreeningPanel · premium edition
 * ---------------------------------------------------------------------
 * Features vs. the classic panel:
 *   1. Every timestamp renders as  dd/mm/yyyy hh:mm  (any input format is
 *      parsed first: ISO 8601, JS Date, "04 Sep 2026 09:41", …).
 *   2. A SOURCE-HIT POPUP (SourceModal): tap any matched source anywhere
 *      (row chip, inline expansion or match-detail modal) and a modal pops
 *      up showing the check message + the source register URL with copy
 *      and "Open register ↗" actions.
 *   3. Responsive: the 4-column desktop grid collapses to stacked,
 *      card-style rows on phones.
 *   4. Premium polish: staggered entrances, status glows, gradient CTAs,
 *      hover-lift links, reduced-motion support, Esc + backdrop close.
 *
 * Data source:
 *   The panel now renders from the API `screening` block (modules, hit
 *   counts, sources in scope, timestamps) — see modulesToRows() below.
 *   Every UI element is identical; only the response → row mapping was
 *   added. If `screening` is absent, the panel falls back to the legacy
 *   decisions/sourcesChecked behaviour, so the prop contract still works.
 */

/* ------------------------------------------------------------------ */
/*  Date helpers  ·  dd/mm/yyyy hh:mm                                  */
/* ------------------------------------------------------------------ */

const MONTHS = {
  jan: 0,
  feb: 1,
  mar: 2,
  apr: 3,
  may: 4,
  jun: 5,
  jul: 6,
  aug: 7,
  sep: 8,
  oct: 9,
  nov: 10,
  dec: 11,
};

function parseDateTime(value) {
  if (value === null || value === undefined || value === '') return null;
  if (value instanceof Date)
    return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value === 'number') return new Date(value);

  const s = String(value)
    .replace(/[·,]/g, ' ') // tolerate "04 Sep 2026 · 09:41"
    .trim()
    .replace(/\s+/g, ' ');
  if (!s) return null;

  // dd/mm/yyyy [hh:mm] or dd-mm-yyyy
  let m = s.match(
    /^(\d{1,2})[/-](\d{1,2})[/-](\d{4})(?:\s+(\d{1,2}):(\d{2})(?::(\d{2}))?)?$/,
  );
  if (m) {
    const [, d, mo, y, hh, mm, ss] = m;
    return new Date(+y, +mo - 1, +d, hh || 0, mm || 0, ss || 0);
  }
  // dd Mon yyyy hh:mm
  m = s.match(
    /^(\d{1,2})\s+([A-Za-z]{3,9})\s+(\d{4})(?:\s+(\d{1,2}):(\d{2})(?::(\d{2}))?)?$/,
  );
  if (m) {
    const [, d, mon, y, hh, mm, ss] = m;
    const mo = MONTHS[mon.slice(0, 3).toLowerCase()];
    if (mo !== undefined)
      return new Date(+y, mo, +d, hh || 0, mm || 0, ss || 0);
  }
  // last resort: native parse (ISO 8601 etc.)
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
}

const pad2 = (n) => String(n).padStart(2, '0');

/** Render any supported date input as dd/mm/yyyy hh:mm. */
export function formatDateTime(value, fallback = '—') {
  const d = parseDateTime(value);
  if (!d) {
    const raw = value && String(value).trim();
    return raw ? raw : fallback;
  }
  return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()} ${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

/* ------------------------------------------------------------------ */
/*  Source registry links                                              */
/* ------------------------------------------------------------------ */

const SOURCE_HREF = {
  'DFAT Consolidated':
    'https://www.dfat.gov.au/international-relations/security/sanctions/consolidated-list',
  DFAT: 'https://www.dfat.gov.au/international-relations/security/sanctions/consolidated-list',
  UN: 'https://main.un.org/securitycouncil/en/sanctions/un-sc-consolidated-list',
  'OFAC SDN': 'https://sanctionssearch.ofac.treas.gov/',
  OFAC: 'https://sanctionssearch.ofac.treas.gov/',
  EU: 'https://data.europa.eu/eli/reg_impl/2024/1313/oj',
  ASIC: 'https://asic.gov.au/',
  RCA: 'https://www.opensanctions.org/',
  PEP: 'https://www.opensanctions.org/',
  'Global PEP': 'https://www.opensanctions.org/',
  'Global adverse media': 'https://www.opensanctions.org/',
  'AU tier 1': 'https://www.afp.gov.au/',
};
const FALLBACK_SEARCH = 'https://www.google.com/search?q=';

/* FIX (hit sources): a hit source may now be the API object
   { source, name, url, percentage } or a bare string (legacy path).
   Normalise both into one shape so the whole UI can rely on it.
   `url` is null when the API gave us nothing — callers must not
   invent one. */
function normaliseHitSource(entry) {
  if (!entry) return null;

  if (typeof entry === 'string') {
    const label = entry.trim();
    return label ? { label, name: null, url: null, percentage: null } : null;
  }
  if (typeof entry !== 'object') return null;

  const label = String(entry.source || entry.name || entry.title || '').trim();
  if (!label) return null;

  const url =
    typeof entry.url === 'string' && entry.url.trim() ? entry.url.trim() : null;
  const name =
    typeof entry.name === 'string' && entry.name.trim()
      ? entry.name.trim()
      : typeof entry.title === 'string' && entry.title.trim()
        ? entry.title.trim()
        : null;

  return {
    label,
    name,
    url,
    percentage: typeof entry.percentage === 'number' ? entry.percentage : null,
  };
}

/* A hit source's label, whether it's the normalised object or a string. */
const hitSourceLabel = (s) =>
  typeof s === 'string' ? s : String(s?.label ?? s?.source ?? s?.name ?? '');

/* FIX (hit sources): resolve the link for a matched source. The URL that
   the API returned on the hit source ALWAYS wins — only fall back to the
   static register map for legacy string sources that carry no url. */
function sourceLink(source) {
  if (source && typeof source === 'object' && source.url) return source.url;

  const name = hitSourceLabel(source);
  const key = name.trim().toLowerCase();
  for (const k of Object.keys(SOURCE_HREF)) {
    if (k.toLowerCase() === key) return SOURCE_HREF[k];
  }
  return FALLBACK_SEARCH + encodeURIComponent(name + ' sanctions PEP list');
}

/* ------------------------------------------------------------------ */
/*  Static configuration (legacy fallback — only used when the        */
/*  `screening` API payload is not available)                          */
/* ------------------------------------------------------------------ */

/** Human explanation per screening check (used by the source-hit modal). */
function checkIntro(key) {
  switch (key) {
    case 'sanctions':
      return 'One or more records matched the international and domestic sanctions registers. Freeze obligations, licensing rules and reporting duties may apply.';
    case 'pep':
      return 'A beneficial owner or signatory matched a politically exposed person record. Enhanced due diligence is required before any business relationship proceeds.';
    case 'adverse_media':
      return 'Recent negative or regulatory coverage was matched for this entity. A compliance analyst must confirm the subject and assess severity.';
    default:
      return 'A screening match was returned for this entity. The record details and register links are shown below.';
  }
}

function sourceAction(row, source) {
  switch (row.key) {
    case 'pep':
      return `Confirm the PEP status against the exact ${source} record and the details shown below.`;
    case 'sanctions':
      return `Confirm the identifiers on the ${source} listing, then apply the appropriate restrictions and document the decision.`;
    case 'adverse_media':
    case 'media':
      return `Open the ${source} record and confirm it concerns this exact entity.`;
    default:
      return `Inspect the matched record directly in the ${source} register.`;
  }
}

const CHECKS = [
  {
    key: 'sanctions',
    label: 'Sanctions',
    sub: 'Restricted & prohibited',
    iconKey: 'sanctions',
    lists: ['DFAT Consolidated', 'UN', 'OFAC SDN', 'EU'],
    watchlist: false,
    date: '2026-09-04T09:41:00', // -> renders 04/09/2026 09:41
  },
  {
    key: 'pep',
    label: 'PEP',
    sub: 'Politically exposed persons',
    iconKey: 'pep',
    lists: ['Global PEP', 'domestic and foreign', 'RCA'],
    watchlist: false,
    date: '2026-09-04T09:41:00',
  },
  {
    key: 'adverse_media',
    label: 'Adverse media',
    sub: 'News & regulatory',
    iconKey: 'adverse_media',
    lists: ['Global adverse media', 'AU tier 1'],
    watchlist: false,
    date: '2026-09-04T09:41:00',
  },
  {
    key: 'watchlist',
    label: 'Watchlist',
    sub: 'Internal watchlist',
    iconKey: 'watchlist',
    lists: ['Module not enabled'],
    watchlist: true,
    date: 'Not checked',
  },
];

/* ------------------------------------------------------------------ */
/*  API screening payload → row model                                  */
/*                                                                     */
/*  Input (from the review response):                                  */
/*    screening: {                                                     */
/*      modules: [{ code, label, description, enabled,                 */
/*                  lists_checked[], result, result_label,             */
/*                  result_detail, last_checked_at }],                 */
/*      hit_count, checks_active, modules_off,                         */
/*      sources_in_scope[], last_screened_at                           */
/*    }                                                                */
/* ------------------------------------------------------------------ */

/* module code → icon glyph (API uses "media" for adverse media) */
const MODULE_ICON = {
  sanctions: 'sanctions',
  pep: 'pep',
  media: 'adverse_media',
  adverse_media: 'adverse_media',
  watchlist: 'watchlist',
};

/* enabled/result → the panel's 3 visual states */
const classifyModule = (m = {}) => {
  const enabled = m.enabled !== false && m.result !== 'module_off';
  if (!enabled) return 'disabled';

  if (m.is_hit === true || m.hit === true) return 'hit';
  const r = String(m.result || '').toLowerCase();
  const saysHit = /hit|match/.test(r) && !/no[\s_-]?hit|no[\s_-]?match/.test(r);
  return saysHit ? 'hit' : 'ok';
};

/* screening.modules[] → the exact row shape the UI renders */
const modulesToRows = (modules) =>
  (Array.isArray(modules) ? modules : []).map((m = {}) => {
    const code = String(m.code || '').toLowerCase();
    const kind = classifyModule(m);
    const enabled = kind !== 'disabled';
    const hit = kind === 'hit';

    /* lists: API codes as-is (OFAC, DFAT, ASIC…); a disabled module gets
       the same "Module not enabled" chip the old UI showed */
    const checkedLists = Array.isArray(m.lists_checked) ? m.lists_checked : [];
    const lists = checkedLists.length
      ? checkedLists
      : !enabled
        ? ['Module not enabled']
        : [];

    /* FIX (hit sources): the API returns `hit_sources` as an array of
       OBJECTS — { source, name, url, percentage } — e.g. the PEP module
       returns the real opensanctions.org entity URL. The old code only
       looked for `matched_sources`/`hits` (neither exists in the payload),
       found nothing, and then FELL BACK TO THE CHECKED LISTS, which
       fabricated fake entries whose links came from the static
       SOURCE_HREF/Google-search guess.

       Now: read hit_sources first, normalise to a consistent object shape
       that carries the API's own url/name/percentage, and render NOTHING
       when the module reports no hit sources. No invented links. */
    const rawHits = Array.isArray(m.hit_sources)
      ? m.hit_sources
      : Array.isArray(m.matched_sources)
        ? m.matched_sources
        : Array.isArray(m.hits)
          ? m.hits
          : [];

    const hitSources = hit
      ? rawHits.map(normaliseHitSource).filter(Boolean)
      : [];

    return {
      key: code || String(m.label || 'check').toLowerCase(),
      label: m.label || code || 'Screening check',
      sub: m.description || '',
      iconKey: MODULE_ICON[code] || 'sanctions',
      lists,
      watchlist: !enabled, // legacy flag: treated as "module off"
      date: m.last_checked_at || null, // ISO — formatted to dd/mm/yyyy hh:mm
      result: m.result_label || (hit ? 'MATCH' : !enabled ? '—' : 'NO HIT'),
      kind,
      detail: hit
        ? m.result_detail ||
          `${m.label || 'This check'} returned a match. Select a source below to inspect its listing.`
        : null,
      hitSources,
    };
  });

const resPill = (kind) =>
  kind === 'hit'
    ? 'bg-red-50 text-red-700 ring-red-200'
    : kind === 'ok'
      ? 'bg-emerald-50 text-emerald-700 ring-emerald-200'
      : kind === 'disabled'
        ? 'bg-slate-100 text-slate-400 ring-slate-200'
        : 'bg-slate-100 text-slate-500 ring-slate-200';

const resDot = (kind) =>
  kind === 'hit'
    ? 'bg-red-500'
    : kind === 'ok'
      ? 'bg-emerald-500'
      : kind === 'disabled'
        ? 'bg-slate-300'
        : 'bg-slate-400';

const COL_LABELS = ['Check', 'Lists checked', 'Result', 'Last checked'];
const COL_GRID = '1.35fr 2.05fr 1.05fr 1.15fr';

/* ------------------------------------------------------------------ */
/*  Keyframe / micro-interaction CSS (scoped, self-contained)          */
/* ------------------------------------------------------------------ */

const PANEL_CSS = `
  .sp-row   { animation: spRowIn .6s cubic-bezier(.22,1,.36,1) both; }
  .sp-fade  { animation: spFade .28s ease-out both; }
  .sp-pop   { animation: spPop .42s cubic-bezier(.22,1,.36,1) both; }
  .sp-dot   { animation: spDot 1.8s ease-in-out infinite; }
  .sp-shimmer { position: relative; overflow: hidden; }
  .sp-shimmer::after {
    content:''; position:absolute; inset:0;
    background: linear-gradient(100deg, transparent 30%, rgba(255,255,255,.35) 50%, transparent 70%);
    transform: translateX(-100%); animation: spShimmer 2.6s ease-in-out infinite;
  }
  .sp-scroll::-webkit-scrollbar { width: 6px; }
  .sp-scroll::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 999px; }
  .sp-scroll::-webkit-scrollbar-track { background: transparent; }
  @keyframes spRowIn  { from { opacity:0; transform: translateY(10px) scale(.992); } to { opacity:1; transform:none; } }
  @keyframes spFade   { from { opacity:0; } to { opacity:1; } }
  @keyframes spPop    { from { opacity:0; transform: translateY(26px) scale(.965); } to { opacity:1; transform:none; } }
  @keyframes spDot    { 0%,100% { opacity:1; transform: scale(1); } 50% { opacity:.45; transform: scale(.72); } }
  @keyframes spShimmer{ to { transform: translateX(100%); } }
  @media (prefers-reduced-motion: reduce) {
    .sp-row, .sp-fade, .sp-pop, .sp-dot, .sp-shimmer::after { animation: none !important; }
  }
`;

/* ------------------------------------------------------------------ */
/*  Small building blocks                                             */
/* ------------------------------------------------------------------ */

const ClockIcon = ({ size = 11, className = 'text-slate-400' }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    className={className}
  >
    <rect x="3" y="4" width="18" height="18" rx="2" />
    <path d="M16 2v4M8 2v4M3 10h18M12 14l2.5-2" />
  </svg>
);

const ExternalIcon = ({ size = 14, className = '' }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d="M7 17L17 7M7 7h10v10" />
  </svg>
);

const LinkIcon = ({ size = 14, className = '' }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    className={className}
  >
    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
  </svg>
);

/* ------------------------------------------------------------------ */
/*  Icon system · custom duotone stroke glyphs + premium badge tiles   */
/* ------------------------------------------------------------------ */

/** Per-check custom glyphs (24px grid, drawn to match, not emoji). */
const glyphPaths = {
  // Sanctions — a globe (international registers) with latitude detail
  sanctions: (
    <>
      <circle cx="12" cy="12" r="9.4" />
      <ellipse cx="12" cy="12" rx="4.1" ry="9.4" />
      <path d="M2.6 12h18.8" />
      <path d="M4.9 7.4a9.3 9.3 0 0 1 14.2 0" className="opacity-40" />
      <path d="M4.9 16.6a9.3 9.3 0 0 0 14.2 0" className="opacity-40" />
    </>
  ),
  // PEP — a person flagged with a verification check
  pep: (
    <>
      <path d="M15.9 21v-2.1a4.1 4.1 0 0 0-4.1-4.1H5.9a4.1 4.1 0 0 0-4.1 4.1V21" />
      <circle cx="8.9" cy="7.2" r="4" />
      <path d="m16.4 11.4 1.8 1.8 3.3-3.9" />
    </>
  ),
  // Adverse media — a front-page article with headline, image + copy
  adverse_media: (
    <>
      <rect x="3.4" y="4.6" width="17.2" height="14.8" rx="2.6" />
      <path d="M8.2 8.8h7" />
      <path d="M8.2 11.6h3.4" className="opacity-50" />
      <rect x="13.6" y="9.8" width="5" height="4.8" rx="1.1" />
      <path d="M8.2 15.2h9" className="opacity-50" />
      <path d="M8.2 17.4h5.6" className="opacity-40" />
    </>
  ),
  // Watchlist — a clipboard with checked lines
  watchlist: (
    <>
      <path d="M16 6.2h1.9A2.1 2.1 0 0 1 20 8.3v10.6a2.1 2.1 0 0 1-2.1 2.1H6.1A2.1 2.1 0 0 1 4 18.9V8.3a2.1 2.1 0 0 1 2.1-2.1H8" />
      <rect x="8" y="2.6" width="8" height="3.6" rx="1.4" />
      <path d="m9.2 12.4.9.9 1.9-2.3" />
      <path d="m9.2 16.2.9.9 1.9-2.3" />
      <path d="M15 12.1h2M15 15.9h2" className="opacity-50" />
    </>
  ),
};

function CheckGlyph({ glyph, size = 22, strokeWidth = 1.7 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {glyphPaths[glyph] || null}
    </svg>
  );
}

const badgeSizes = {
  sm: 'w-9 h-9 rounded-xl',
  md: 'w-[42px] h-[42px] rounded-[14px]',
  mobile: 'w-11 h-11 rounded-[15px]',
  modal: 'w-10 h-10 rounded-2xl',
};

const badgeTint = (kind) =>
  kind === 'hit'
    ? 'text-rose-600 bg-gradient-to-br from-rose-50 to-red-100/70 ring-rose-200/80'
    : kind === 'ok'
      ? 'text-emerald-600 bg-gradient-to-br from-emerald-50 to-teal-50 ring-emerald-200/80'
      : 'text-slate-400 bg-gradient-to-br from-slate-50 to-slate-200/70 ring-slate-200/80';

/**
 * Premium icon badge. `kind` drives tint/glow; `solid` gives a filled
 * gradient tile (used in modal headers) with a white glyph.
 */
function IconBadge({
  glyph,
  kind = 'ok',
  size = 'md',
  solid = false,
  glow = false,
  className = '',
}) {
  const face = solid
    ? kind === 'hit'
      ? 'bg-gradient-to-br from-[#E11D48] to-[#FB7185] text-white ring-[#FDA4AF]/70'
      : 'bg-gradient-to-br from-[#375AE7] to-[#6C8CFF] text-white ring-[#C7D6FF]/80'
    : badgeTint(kind);

  const shadow = glow
    ? kind === 'hit'
      ? '0 14px 30px -12px rgba(225,29,72,.55), inset 0 1px 0 rgba(255,255,255,.5)'
      : '0 14px 30px -12px rgba(55,90,231,.5), inset 0 1px 0 rgba(255,255,255,.5)'
    : 'inset 0 1px 0 rgba(255,255,255,.65), 0 1px 3px rgba(15,23,42,.08)';

  return (
    <span
      className={`inline-flex items-center justify-center shrink-0 ring-1 transition-transform duration-300 ${
        badgeSizes[size] || badgeSizes.md
      } ${face} ${className}`}
      style={{ boxShadow: shadow }}
    >
      <CheckGlyph
        glyph={glyph}
        size={solid ? 23 : 22}
        strokeWidth={solid ? 1.9 : 1.7}
      />
    </span>
  );
}

const ShieldIcon = ({ size = 18, ok = true }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={ok ? 2 : 2}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M12 22s8-3.5 8-10V5l-8-3-8 3v7c0 6.5 8 10 8 10z" />
    {ok ? (
      <path d="m9 12.1 2.1 2.1 4.1-4.6" />
    ) : (
      <>
        <path d="M12 8.4v4.4" />
        <path d="M12 16.3h.01" />
      </>
    )}
  </svg>
);

const WarningGlyph = ({ size = 14, className = '' }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    aria-hidden="true"
  >
    <path d="M12 3.6 2.9 19.3a1.7 1.7 0 0 0 1.5 2.6h15.2a1.7 1.7 0 0 0 1.5-2.6L12 3.6Z" />
    <path d="M12 9.8v4.4" />
    <path d="M12 17.3h.01" />
  </svg>
);

const ShieldCheckGlyph = ({ size = 15, className = '' }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.8}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    aria-hidden="true"
  >
    <path d="M12 21.6S4.6 18.2 4.6 12.2V5.7L12 2.8l7.4 2.9v6.5c0 6-7.4 9.4-7.4 9.4z" />
    <path d="m8.7 11.7 2.2 2.2 4.4-4.8" />
  </svg>
);

/** Copy helper with legacy fallback. */
function copyText(t) {
  if (navigator.clipboard && navigator.clipboard.writeText) {
    return navigator.clipboard.writeText(t);
  }
  return new Promise((resolve) => {
    try {
      const ta = document.createElement('textarea');
      ta.value = t;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      resolve(document.execCommand('copy'));
      document.body.removeChild(ta);
    } catch {
      resolve(false);
    }
  });
}

/* ------------------------------------------------------------------ */
/*  Main component                                                     */
/* ------------------------------------------------------------------ */

export default function ScreeningPanel({
  decisions = {},
  sourcesChecked = [],
  matchedSources = [],
  latest = null,
  sanctionsResults = null, // review data — `screening` is read from here
  screeningDate = '-',
  screening: screeningProp = null, // or passed directly by the parent
}) {
  const [filter, setFilter] = useState('all');
  const [openKey, setOpenKey] = useState(null); // expanded inline detail
  const [matchKey, setMatchKey] = useState(null); // match-detail modal (row key)
  const [sourceFocus, setSourceFocus] = useState(null); // { rowKey, source }

  /* The API screening block — explicit prop wins, otherwise it's read
     from sanctions_checks (which the parent already passes through). */
  const screening = screeningProp || sanctionsResults?.screening || null;
  const hasScreening =
    Array.isArray(screening?.modules) && screening.modules.length > 0;

  /* Rows — API modules when present, legacy decision-driven otherwise.
     Row shape is identical either way, so the UI below never changes. */
  const checks = useMemo(() => {
    if (hasScreening) return modulesToRows(screening.modules);
    return CHECKS.map((c) => {
      let result,
        kind,
        detail = null,
        hitSources = [];
      if (c.watchlist) {
        result = '—';
        kind = 'disabled';
      } else {
        const hit = !!decisions[c.key];
        result = hit ? (c.key === 'pep' ? '1 MATCH' : 'MATCH') : 'NO HIT';
        kind = hit ? 'hit' : 'ok';
        if (hit) {
          hitSources = (matchedSources || []).length
            ? matchedSources
            : sourcesChecked;
          detail =
            c.key === 'pep'
              ? 'Politically exposed person match on a beneficial owner. Review the matched records below for confirmation.'
              : `${(matchedSources || []).length} source${(matchedSources || []).length === 1 ? '' : 's'} returned a match. Select a source below to inspect its listing.`;
        }
      }
      return { ...c, result, kind, detail, hitSources };
    });
  }, [hasScreening, screening, decisions, matchedSources, sourcesChecked]);

  /* Header hit counter — API hit_count when available */
  const hits = useMemo(() => {
    if (hasScreening && typeof screening?.hit_count === 'number')
      return screening.hit_count;
    return checks.filter((c) => c.kind === 'hit').length;
  }, [hasScreening, screening, checks]);

  const visible = checks.filter((c) =>
    filter === 'hits' ? c.kind === 'hit' : true,
  );
  const activeMatch = checks.find((c) => c.key === matchKey);
  const activeFocus =
    sourceFocus && checks.find((c) => c.key === sourceFocus.rowKey);
  const focusSource = sourceFocus && sourceFocus.source;
  const firstHit = checks.find((c) => c.kind === 'hit');

  /* Context strip + subtitle — mapped from the API summary fields */
  const lastScreenedInput = hasScreening
    ? screening?.last_screened_at || screeningDate
    : screeningDate !== '-'
      ? screeningDate
      : latest?.response_timestamp || latest;
  const checkedAt = formatDateTime(lastScreenedInput);
  const activeChecks = hasScreening
    ? (screening?.checks_active ??
      checks.filter((c) => c.kind !== 'disabled').length)
    : checks.filter((c) => !c.watchlist).length;
  const modulesOff = hasScreening
    ? (screening?.modules_off ??
      checks.filter((c) => c.kind === 'disabled').length)
    : checks.filter((c) => !!c.watchlist).length;
  const scopeSources = hasScreening
    ? Array.isArray(screening?.sources_in_scope)
      ? screening.sources_in_scope
      : sourcesChecked
    : sourcesChecked;
  const checksSubtitle = hasScreening
    ? checks.map((c) => c.label).join(' · ')
    : 'Sanctions · PEP · Adverse media · Watchlist';

  // Esc closes the top-most modal only · body scroll lock while open
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      if (sourceFocus) setSourceFocus(null);
      else if (matchKey) setMatchKey(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [sourceFocus, matchKey]);

  useEffect(() => {
    const locked = !!(matchKey || sourceFocus);
    if (!locked) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [matchKey, sourceFocus]);

  const openSource = (row, source) =>
    setSourceFocus({ rowKey: row.key, source });

  return (
    <div className="rounded-[22px] border border-slate-200 bg-white shadow-[0_1px_2px_rgba(15,23,42,.05),0_24px_48px_-28px_rgba(15,23,42,.22)] overflow-hidden">
      <style>{PANEL_CSS}</style>

      {/* ---------- Header ---------- */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-x-0 top-0 h-[3px] bg-gradient-to-r from-[#375AE7] via-[#6C8CFF] to-[#22C55E]" />
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3.5 px-5 py-4">
          <div className="flex items-center gap-3.5 min-w-0">
            <span
              className={`relative w-12 h-12 rounded-2xl flex items-center justify-center shrink-0 text-white transition-all duration-300 ${hits ? 'bg-gradient-to-br from-[#E11D48] via-[#F43F5E] to-[#FB7185]' : 'bg-gradient-to-br from-[#375AE7] via-[#5B7CFA] to-[#6C8CFF]'}`}
              style={{
                boxShadow: hits
                  ? '0 14px 30px -10px rgba(225,29,72,.6), inset 0 1px 0 rgba(255,255,255,.35)'
                  : '0 14px 30px -10px rgba(55,90,231,.6), inset 0 1px 0 rgba(255,255,255,.35)',
              }}
            >
              <span className="drop-shadow-[0_1px_2px_rgba(15,23,42,.35)]">
                <ShieldIcon size={23} ok={!hits} />
              </span>
              {hits > 0 && (
                <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-white ring-1 ring-slate-100 flex items-center justify-center">
                  <span className="w-2 h-2 rounded-full bg-red-500 sp-dot" />
                </span>
              )}
            </span>
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <h3 className="text-[16px] font-extrabold tracking-tight text-slate-900">
                  Screening
                </h3>
                <span
                  className={`inline-flex items-center gap-1.5 font-extrabold text-[10.5px] rounded-full px-2.5 py-1 ring-1 shrink-0 ${hits ? 'bg-red-50 text-red-600 ring-red-200' : 'bg-emerald-50 text-emerald-600 ring-emerald-200'}`}
                >
                  <span
                    className={`w-1.5 h-1.5 rounded-full ${hits ? 'bg-red-500 sp-dot' : 'bg-emerald-500'}`}
                  />
                  {hits} {hits === 1 ? 'HIT' : 'HITS'}
                </span>
              </div>
              <p className="text-[11px] font-semibold text-slate-400 mt-0.5">
                {checksSubtitle}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            {firstHit && (
              <button
                onClick={() => setMatchKey(firstHit.key)}
                className="inline-flex items-center gap-1.5 text-xs font-bold rounded-full px-4 py-2.5 bg-gradient-to-r from-[#375AE7] to-[#5B7CFA] text-white shadow-[0_10px_22px_-10px_rgba(55,90,231,.7)] hover:-translate-y-px hover:shadow-[0_14px_26px_-10px_rgba(55,90,231,.8)] active:translate-y-0 active:scale-[0.98] transition-all"
              >
                View match detail
                <ExternalIcon size={12} />
              </button>
            )}
            <div className="flex items-center gap-1 rounded-full bg-white p-1 ring-1 ring-slate-200 w-fit shadow-sm">
              {['all', 'hits'].map((f) => (
                <button
                  key={f}
                  onClick={() => setFilter(f)}
                  aria-pressed={filter === f}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-full transition-all ${
                    filter === f
                      ? 'bg-gradient-to-r from-[#375AE7] to-[#5B7CFA] text-white shadow-sm'
                      : 'text-slate-500 hover:text-slate-800'
                  }`}
                >
                  {f === 'all' ? 'All' : 'Hits only'}
                  {f === 'hits' && hits > 0 && (
                    <span className="text-[10px] font-bold rounded-full px-1.5 bg-white/25 text-white">
                      {hits}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Context strip — overall screening state */}
        <div className="px-5 pb-3.5 -mt-0.5">
          <div
            className={`flex flex-wrap items-center gap-x-4 gap-y-1.5 rounded-2xl px-4 py-2.5 ring-1 text-[11px] font-semibold ${
              hits
                ? 'bg-gradient-to-r from-red-50/80 to-amber-50/40 ring-red-100 text-red-700'
                : 'bg-gradient-to-r from-emerald-50/70 to-white ring-emerald-100 text-emerald-700'
            }`}
          >
            <span className="inline-flex items-center gap-1.5">
              <ClockIcon size={11} />
              Last screened {checkedAt}
            </span>
            <span className="text-slate-300">•</span>
            <span>
              {activeChecks} checks active · {modulesOff} module
              {modulesOff === 1 ? '' : 's'} off
            </span>
            <span className="text-slate-300">•</span>
            <span className={hits ? 'text-red-600' : ''}>
              {hits > 0
                ? `${hits} match${hits === 1 ? '' : 'es'} detected — select a source to view details`
                : 'All sources cleared'}
            </span>
          </div>
        </div>
      </div>

      {/* ---------- Column labels (≥sm) ---------- */}
      <div
        className="hidden sm:grid border-b border-slate-100 bg-gradient-to-r from-[#FAFBFE] to-[#F3F6FE]"
        style={{ gridTemplateColumns: COL_GRID }}
      >
        {COL_LABELS.map((h) => (
          <span
            key={h}
            className="px-5 py-3 text-[10.5px] font-extrabold uppercase tracking-wider text-slate-400"
          >
            {h}
          </span>
        ))}
      </div>

      {/* ---------- Rows ---------- */}
      <div className="divide-y divide-slate-100">
        {visible.length === 0 && (
          <div className="py-12 text-center">
            <span className="mx-auto mb-2.5 flex items-center justify-center">
              <IconBadge glyph="sanctions" kind="ok" size="mobile" glow />
            </span>
            <p className="text-sm font-semibold text-slate-500">
              No screening hits
            </p>
            <p className="text-xs text-slate-400 mt-1">
              Nothing matched across the monitored registers.
            </p>
          </div>
        )}
        {visible.map((c, idx) => (
          <ScreeningRow
            key={c.key}
            row={c}
            idx={idx}
            grid={COL_GRID}
            open={openKey === c.key}
            onToggle={() => setOpenKey(openKey === c.key ? null : c.key)}
            onOpenMatch={() => setMatchKey(c.key)}
            onOpenSource={(source) => openSource(c, source)}
          />
        ))}
      </div>

      {/* ---------- Footer ---------- */}
      <div className="flex flex-wrap items-center justify-between gap-2 px-5 py-3 border-t border-slate-100 bg-gradient-to-r from-slate-50/70 to-white">
        <p className="text-[10.5px] font-semibold text-slate-400">
          Linked records open in a new tab · Data refreshes automatically
        </p>
        <p className="text-[10.5px] font-semibold text-slate-400">
          {scopeSources.length > 0 &&
            `${scopeSources.length} source${scopeSources.length === 1 ? '' : 's'} in scope`}
        </p>
      </div>

      {/* ---------- Modals ---------- */}
      {activeMatch && (
        <MatchDetailModal
          row={activeMatch}
          onClose={() => setMatchKey(null)}
          onOpenSource={openSource}
          onOpenRow={() => setOpenKey(activeMatch.key)}
        />
      )}
      {activeFocus && focusSource && (
        <SourceModal
          row={activeFocus}
          source={focusSource}
          onClose={() => setSourceFocus(null)}
        />
      )}
    </div>
  );
}

/* ================================================================== */
/*  Row (shared by desktop grid + stacked mobile card)                 */
/* ================================================================== */

function ScreeningRow({
  row,
  idx,
  grid,
  open,
  onToggle,
  onOpenMatch,
  onOpenSource,
}) {
  const hasHit = row.kind === 'hit';
  const isDisabled = row.kind === 'disabled';
  const isClean = row.kind === 'ok';
  /* FIX (hit sources): hitSources are now objects, so match on their
     label rather than stringifying the object (which produced
     "[object Object]" and made every chip look unmatched). */
  const matchedNames = useMemo(
    () =>
      new Set(
        (row.hitSources || [])
          .map((s) => hitSourceLabel(s).trim().toLowerCase())
          .filter(Boolean),
      ),
    [row.hitSources],
  );
  const matched = (name) => matchedNames.has(String(name).trim().toLowerCase());

  /* FIX (hit sources): a "Lists checked" chip is a bare list code (OFAC,
     PEP…). When it matched, open the corresponding hit-source OBJECT so
     the modal gets the API's real url instead of a guessed one. */
  const hitSourceFor = (name) => {
    const key = String(name).trim().toLowerCase();
    return (
      (row.hitSources || []).find(
        (s) => hitSourceLabel(s).trim().toLowerCase() === key,
      ) || name
    );
  };

  const dateText = isDisabled ? 'Not checked' : formatDateTime(row.date);

  const rowEvents = hasHit
    ? {
        onClick: onToggle,
        onKeyDown: (e) => {
          if (e.target !== e.currentTarget) return;
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onToggle();
          }
        },
        role: 'button',
        'aria-expanded': open,
        tabIndex: 0,
      }
    : {};

  return (
    <div
      className={`relative transition-colors ${hasHit ? 'group' : ''} ${
        hasHit && !isDisabled
          ? open
            ? 'bg-red-50/30'
            : 'hover:bg-red-50/25'
          : ''
      }`}
      style={{ animationDelay: `${idx * 70}ms` }}
      {...rowEvents}
    >
      <div className="sp-row">
        {hasHit && !isDisabled && (
          <span className="absolute left-0 top-0 bottom-0 w-[4px] rounded-r-full bg-gradient-to-b from-[#E11D48] to-[#FB7185]" />
        )}

        {/* ---------- DESKTOP 4-col row ---------- */}
        <div
          className="hidden sm:grid items-center gap-4 px-5 py-4 pl-6"
          style={{ gridTemplateColumns: grid }}
        >
          {/* Check */}
          <Cell>
            <div className="flex items-center gap-3 min-w-0">
              <IconBadge
                glyph={row.iconKey}
                kind={row.kind}
                size="md"
                glow={hasHit}
                className={hasHit ? 'group-hover:scale-[1.07]' : ''}
              />
              <div className="min-w-0">
                <p
                  className={`text-sm font-bold leading-tight ${isDisabled ? 'text-slate-400' : 'text-slate-900'}`}
                >
                  {row.label}
                </p>
                {row.sub && (
                  <p className="text-[11px] font-semibold text-slate-400 mt-0.5 truncate">
                    {row.sub}
                  </p>
                )}
              </div>
            </div>
          </Cell>

          {/* Lists checked */}
          <Cell>
            <div className="flex flex-wrap gap-1.5">
              {row.lists.map((l) => (
                <ListChip
                  key={l}
                  label={l}
                  clickable={hasHit && matched(l)}
                  matched={matched(l)}
                  onOpen={() => onOpenSource(hitSourceFor(l))}
                />
              ))}
            </div>
          </Cell>

          {/* Result */}
          <Cell>
            <div className="flex flex-col items-start gap-1">
              <StatusPill row={row} />
              {/* FIX (hit sources): don't fall back to lists.length — a hit
                  with no reported sources must not claim every list matched.
                  `result_detail` (e.g. "Not Verified") is shown instead. */}
              <span className="text-[10px] font-semibold text-slate-400">
                {hasHit
                  ? row.hitSources.length
                    ? `${row.hitSources.length} of ${row.lists.length} checked matched`
                    : row.detail || 'Match detected'
                  : isDisabled
                    ? 'Module off'
                    : 'All registers cleared'}
              </span>
            </div>
          </Cell>

          {/* Checked */}
          <Cell>
            <div className="flex flex-col items-start gap-2">
              <span
                className={`inline-flex items-center gap-1.5 text-[11.5px] font-semibold whitespace-nowrap ${isDisabled ? 'text-slate-300' : 'text-slate-600'}`}
              >
                <ClockIcon size={11} />
                {dateText}
              </span>
              {hasHit ? (
                <div className="flex items-center gap-1.5">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onOpenMatch();
                    }}
                    className="inline-flex items-center gap-1 text-[11px] font-bold text-[#375AE7] bg-[#EEF2FF] hover:bg-[#375AE7] hover:text-white rounded-full px-2.5 py-1 transition-all"
                  >
                    Full detail
                    <ExternalIcon size={9} />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onToggle();
                    }}
                    aria-label={
                      open ? 'Collapse match detail' : 'Expand match detail'
                    }
                    className={`w-6 h-6 inline-flex items-center justify-center rounded-full ring-1 ring-slate-200 text-slate-500 hover:text-[#375AE7] hover:ring-brand/40 transition-all ${
                      open ? 'rotate-180 bg-[#EEF2FF] text-[#375AE7]' : ''
                    }`}
                  >
                    <svg
                      width="11"
                      height="11"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                    >
                      <path d="M6 9l6 6 6-6" />
                    </svg>
                  </button>
                </div>
              ) : (
                <span className="text-slate-200 text-[11px]">—</span>
              )}
            </div>
          </Cell>
        </div>

        {/* ---------- MOBILE stacked card ---------- */}
        <div className="sm:hidden px-4 py-4 pl-5">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <IconBadge
                glyph={row.iconKey}
                kind={row.kind}
                size="mobile"
                glow={hasHit}
              />
              <div className="min-w-0">
                <p
                  className={`text-sm font-bold leading-tight ${isDisabled ? 'text-slate-400' : 'text-slate-900'}`}
                >
                  {row.label}
                </p>
                <p className="text-[11px] font-semibold text-slate-400 mt-0.5">
                  {row.sub}
                </p>
              </div>
            </div>
            <StatusPill row={row} />
          </div>

          <div className="flex flex-wrap gap-1.5 mt-3">
            {row.lists.map((l) => (
              <ListChip
                key={l}
                label={l}
                matched={matched(l)}
                clickable={hasHit && matched(l)}
                onOpen={() => onOpenSource(hitSourceFor(l))}
              />
            ))}
          </div>

          <div className="flex items-center justify-between gap-2 mt-3 pt-3 border-t border-dashed border-slate-100">
            <span
              className={`inline-flex items-center gap-1.5 text-[11px] font-semibold ${isDisabled ? 'text-slate-300' : 'text-slate-500'}`}
            >
              <ClockIcon size={11} />
              {dateText}
            </span>
            {hasHit && (
              <div className="flex items-center gap-1.5">
                <button
                  onClick={onOpenMatch}
                  className="inline-flex items-center gap-1 text-[11px] font-bold text-[#375AE7] bg-[#EEF2FF] hover:bg-[#375AE7] hover:text-white rounded-full px-3 py-1.5 transition-all"
                >
                  Full detail
                  <ExternalIcon size={9} />
                </button>
                <button
                  onClick={onToggle}
                  aria-label={
                    open ? 'Collapse match detail' : 'Expand match detail'
                  }
                  className={`w-7 h-7 inline-flex items-center justify-center rounded-full ring-1 ring-slate-200 text-slate-500 transition-all ${
                    open ? 'rotate-180 bg-[#EEF2FF] text-[#375AE7]' : ''
                  }`}
                >
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                  >
                    <path d="M6 9l6 6 6-6" />
                  </svg>
                </button>
              </div>
            )}
          </div>
        </div>

        {/* ---------- Inline expandable match detail ---------- */}
        {hasHit && open && (
          <div className="px-4 sm:px-5 pb-4 ml-1 sm:ml-1.5 sp-pop">
            <div className="relative rounded-2xl border border-red-200/80 bg-gradient-to-br from-red-50/60 to-rose-50/30 px-4 py-3.5 overflow-hidden">
              <span className="absolute left-0 top-0 bottom-0 w-[3px] bg-gradient-to-b from-[#E11D48] to-[#FB7185]" />
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h5 className="text-[12px] font-extrabold text-red-700 flex items-center gap-1.5">
                  <WarningGlyph size={14} className="text-red-500" />
                  {row.label} — match detail
                </h5>
                <button
                  onClick={onOpenMatch}
                  className="inline-flex items-center gap-1 text-[10.5px] font-bold text-[#375AE7] hover:underline"
                >
                  Open full detail <ExternalIcon size={10} />
                </button>
              </div>
              <p className="text-[11.5px] text-red-900/70 leading-relaxed mt-1.5">
                {row.detail}
              </p>
              {/* FIX (hit sources): render the matched entity name and the
                  API match score, and only offer "view source" when the
                  API actually gave us a url. */}
              {(row.hitSources || []).length > 0 && (
                <div className="flex flex-wrap gap-2 mt-3">
                  {row.hitSources.map((s, i) => {
                    const label = hitSourceLabel(s);
                    const entity = typeof s === 'object' ? s.name : null;
                    const pct = typeof s === 'object' ? s.percentage : null;
                    const hasUrl = typeof s === 'object' ? !!s.url : true;
                    return (
                      <button
                        key={`${label}-${entity || ''}-${i}`}
                        onClick={() => onOpenSource(s)}
                        className="inline-flex items-center gap-1.5 text-[10.5px] font-bold text-[#375AE7] bg-white ring-1 ring-slate-200/80 rounded-full px-3 py-1.5 shadow-sm hover:ring-[#375AE7] hover:-translate-y-px hover:shadow-md transition-all"
                      >
                        <LinkIcon size={11} />
                        {label}
                        {entity && (
                          <>
                            <span className="text-slate-300">·</span>
                            <span className="text-slate-700 normal-case font-semibold">
                              {entity}
                            </span>
                          </>
                        )}
                        {typeof pct === 'number' && (
                          <span className="text-[9.5px] font-extrabold text-red-600 bg-red-50 ring-1 ring-red-100 rounded-full px-1.5">
                            {pct}%
                          </span>
                        )}
                        {hasUrl && (
                          <>
                            <span className="text-slate-300">·</span>
                            <span className="text-slate-500 normal-case font-semibold">
                              view source
                            </span>
                          </>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ================================================================== */
/*  Tiny presentational helpers                                        */
/* ================================================================== */

const Cell = ({ children, className = '' }) => (
  <div className={`min-w-0 ${className}`}>{children}</div>
);

function StatusPill({ row }) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 text-[11px] font-extrabold rounded-full px-3 py-1 ring-1 ${resPill(row.kind)}`}
    >
      <span
        className={`w-1.5 h-1.5 rounded-full ${resDot(row.kind)} ${row.kind === 'hit' ? 'sp-dot' : ''}`}
      />
      {row.result}
    </span>
  );
}

function ListChip({ label, clickable = false, matched = false, onOpen }) {
  if (clickable) {
    return (
      <button
        onClick={onOpen}
        title={`View ${label} source details`}
        className="inline-flex items-center gap-1.5 text-[10.5px] font-bold text-red-700 bg-red-50 ring-1 ring-red-200/80 rounded-full px-2.5 py-1 shadow-sm hover:bg-red-100 hover:-translate-y-px hover:shadow-md transition-all cursor-pointer"
      >
        <LinkIcon size={11} />
        {label}
      </button>
    );
  }
  return (
    <span
      className={`inline-flex items-center gap-1.5 text-[10.5px] font-semibold rounded-full px-2.5 py-1 ring-1 shadow-sm ${
        matched
          ? 'text-red-700 bg-red-50/60 ring-red-200/70'
          : 'text-slate-600 bg-white ring-slate-100'
      }`}
    >
      <span
        className={`w-1 h-1 rounded-full ${matched ? 'bg-red-400' : 'bg-slate-300'}`}
      />
      {label}
    </span>
  );
}

/* ================================================================== */
/*  Shared modal shell (backdrop · pop animation · portal-free)        */
/* ================================================================== */

function ModalShell({ onClose, label, children, maxW = 'sm:max-w-xl' }) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={label}
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
    >
      <div
        className="absolute inset-0 bg-[#0B1220]/55 backdrop-blur-[6px] sp-fade"
        onClick={onClose}
      />
      <div
        className={`relative w-full ${maxW} bg-white rounded-t-[26px] sm:rounded-[22px] shadow-[0_40px_90px_-30px_rgba(10,15,35,.5)] sp-pop max-h-[92vh] flex flex-col overflow-hidden`}
      >
        {children}
      </div>
    </div>
  );
}

/* ================================================================== */
/*  Match-detail modal (all hits + list of matched sources)            */
/* ================================================================== */

function MatchDetailModal({ row, onClose, onOpenSource }) {
  const matched = (row.hitSources || []).filter(Boolean);
  const dateText = formatDateTime(row.date);

  return (
    <ModalShell onClose={onClose} label={`${row.label} match detail`}>
      {/* header */}
      <div className="relative shrink-0">
        <div className="absolute inset-x-0 top-0 h-[3px] bg-gradient-to-r from-[#E11D48] to-[#FB7185]" />
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <div className="flex items-center gap-3 min-w-0">
            <IconBadge glyph={row.iconKey} kind="hit" size="modal" solid glow />
            <div className="min-w-0">
              <h3 className="text-sm font-extrabold text-slate-900">
                {row.label} · match detail
              </h3>
              <p className="text-[11px] font-semibold text-slate-400 flex items-center gap-1.5 mt-0.5">
                <ClockIcon size={10} /> Checked {dateText}
              </p>
            </div>
          </div>
          <CloseButton onClick={onClose} />
        </div>
      </div>

      {/* body */}
      <div className="px-5 py-4 overflow-y-auto sp-scroll">
        {/* result banner */}
        <div className="relative rounded-2xl border border-red-200/90 bg-gradient-to-br from-red-50 to-rose-50/40 px-4 py-3.5 overflow-hidden">
          <span className="absolute left-0 top-0 bottom-0 w-[4px] bg-gradient-to-b from-[#E11D48] to-[#FB7185]" />
          <div className="flex items-center gap-2">
            <StatusPill row={row} />
            <span className="text-[10.5px] font-bold text-red-400 uppercase tracking-wide">
              Match detected · details below
            </span>
          </div>
          <p className="text-[12px] text-red-900/75 leading-relaxed mt-2">
            {row.detail}
          </p>
          <p className="text-[10.5px] font-semibold text-red-500/80 mt-2.5 leading-relaxed">
            Registers checked:{' '}
            {row.lists.map((l) => (
              <span
                key={l}
                className="inline-block bg-white/70 ring-1 ring-red-100 rounded-full px-2 py-0.5 text-[10px] font-bold text-red-700 mx-0.5"
              >
                {l}
              </span>
            ))}
          </p>
        </div>

        {/* matched sources */}
        <p className="text-[10.5px] font-extrabold uppercase tracking-wider text-slate-400 mt-4 mb-2 flex items-center gap-2">
          Matched sources
          {matched.length > 0 && (
            <span className="text-[9.5px] font-bold bg-red-50 text-red-600 ring-1 ring-red-100 rounded-full px-2 py-0.5">
              {matched.length}
            </span>
          )}
        </p>

        {/* FIX (hit sources): when the module reported a hit but returned no
            hit_sources (e.g. the Identity check, which is "Not Verified"
            with no register to link to), show nothing rather than inventing
            links from the checked lists. */}
        {matched.length === 0 ? (
          <div className="rounded-xl border border-dashed border-slate-200 px-4 py-6 text-center">
            <p className="text-[11.5px] font-semibold text-slate-400">
              This check reported no linkable source records.
            </p>
            <p className="text-[10.5px] text-slate-300 mt-0.5">
              {row.detail || 'Review the check result above.'}
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {matched.map((src, i) => (
              <SourceCard
                key={`${hitSourceLabel(src)}-${i}`}
                source={src}
                onOpen={() => onOpenSource(row, src)}
              />
            ))}
          </div>
        )}

        <p className="text-[10px] text-slate-300 mt-3">
          Tip — tap a source to inspect its register URL, or use the arrow to
          open it directly.
        </p>
      </div>

      {/* footer — informational only, no approval step */}
      <div className="flex flex-wrap items-center justify-between gap-2 px-5 py-3.5 border-t border-slate-100 bg-slate-50/60 shrink-0">
        <p className="text-[10.5px] font-semibold text-slate-400">
          Open a source register to confirm the match record directly.
        </p>
        <button
          onClick={onClose}
          className="inline-flex items-center gap-2 h-10 px-5 rounded-xl bg-gradient-to-r from-[#375AE7] to-[#5B7CFA] text-white text-[13px] font-bold shadow-[0_12px_24px_-12px_rgba(55,90,231,.8)] hover:-translate-y-0.5 transition-all active:scale-[0.97]"
        >
          Done
          <svg
            width="13"
            height="13"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
          >
            <path d="M20 6L9 17l-5-5" />
          </svg>
        </button>
      </div>
    </ModalShell>
  );
}

/** One matched source → opens the source-hit popup · trailing arrow = direct open. */
function SourceCard({ source, onOpen }) {
  const href = sourceLink(source);
  /* FIX (hit sources): show the matched entity name + score from the API
     object; fall back to the generic hint for legacy string sources. */
  const label = hitSourceLabel(source);
  const entity = source && typeof source === 'object' ? source.name : null;
  const pct =
    source &&
    typeof source === 'object' &&
    typeof source.percentage === 'number'
      ? source.percentage
      : null;
  return (
    <div className="group rounded-xl border border-slate-200 bg-white transition-all hover:border-[#375AE7]/35 hover:shadow-[0_10px_24px_-16px_rgba(15,23,42,.35)]">
      <div className="flex items-stretch">
        <button
          onClick={onOpen}
          className="flex-1 flex items-center gap-3 px-3.5 py-3 text-left min-w-0"
        >
          <span className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#EEF2FF] to-[#E3EAFF] text-[#375AE7] flex items-center justify-center shrink-0 ring-1 ring-[#DCE4FB]">
            <LinkIcon size={14} />
          </span>
          <span className="min-w-0 flex-1">
            <span className="flex items-center gap-1.5 min-w-0">
              <span className="text-[12.5px] font-bold text-slate-800 group-hover:text-[#375AE7] truncate">
                {entity || label}
              </span>
              {pct !== null && (
                <span className="shrink-0 text-[9.5px] font-extrabold text-red-600 bg-red-50 ring-1 ring-red-100 rounded-full px-1.5 py-0.5">
                  {pct}%
                </span>
              )}
            </span>
            <span className="block text-[10.5px] text-slate-400 truncate">
              {entity
                ? `${label} · tap to view listing & register link`
                : 'Tap to view listing & register link'}
            </span>
          </span>
          <span className="inline-flex items-center gap-1 text-[10.5px] font-extrabold text-[#375AE7] opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
            Details
            <svg
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
            >
              <path d="M5 12h14M13 6l6 6-6 6" />
            </svg>
          </span>
        </button>
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          title="Open register in a new tab"
          className="flex items-center justify-center w-11 border-l border-slate-100 text-slate-300 hover:text-[#375AE7] hover:bg-[#EEF2FF]/60 transition-colors rounded-r-xl"
        >
          <ExternalIcon size={13} />
        </a>
      </div>
    </div>
  );
}

/* ================================================================== */
/*  Source-hit popup  ·  message + source link URL (+ copy)            */
/* ================================================================== */

function SourceModal({ row, source, onClose }) {
  const [copied, setCopied] = useState(false);
  const href = sourceLink(source);
  const dateText = formatDateTime(row.date);
  /* FIX (hit sources): the source may be the API object, so pull out the
     register label, matched entity and score for display. */
  const label = hitSourceLabel(source);
  const entity = source && typeof source === 'object' ? source.name : null;
  const pct =
    source &&
    typeof source === 'object' &&
    typeof source.percentage === 'number'
      ? source.percentage
      : null;

  const handleCopy = async (e) => {
    e.stopPropagation();
    await copyText(href);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1800);
  };

  return (
    <ModalShell
      onClose={onClose}
      label={`${label} source match`}
      maxW="sm:max-w-lg"
    >
      {/* header */}
      <div className="relative shrink-0">
        <div className="absolute inset-x-0 top-0 h-[3px] bg-gradient-to-r from-[#375AE7] via-[#6C8CFF] to-[#22D3EE]" />
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <div className="flex items-center gap-3 min-w-0">
            <span className="w-10 h-10 rounded-2xl bg-gradient-to-br from-[#375AE7] to-[#6C8CFF] text-white flex items-center justify-center shadow-[0_8px_20px_-8px_rgba(55,90,231,.65)]">
              <LinkIcon size={16} />
            </span>
            <div className="min-w-0">
              <h3 className="text-sm font-extrabold text-slate-900 truncate">
                {entity || label}
              </h3>
              <p className="text-[11px] font-semibold text-slate-400 mt-0.5 flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-red-500 sp-dot" />
                {label} match · {row.label} check
                {pct !== null && (
                  <span className="text-[9.5px] font-extrabold text-red-600 bg-red-50 ring-1 ring-red-100 rounded-full px-1.5 py-0.5">
                    {pct}% match
                  </span>
                )}
              </p>
            </div>
          </div>
          <CloseButton onClick={onClose} />
        </div>
      </div>

      {/* body */}
      <div className="px-5 py-4 overflow-y-auto sp-scroll">
        {/* message */}
        <div className="relative rounded-2xl border border-slate-200 bg-gradient-to-br from-slate-50 to-[#F6F8FF] px-4 py-3.5 overflow-hidden">
          <span className="absolute left-0 top-0 bottom-0 w-[3px] bg-gradient-to-b from-[#375AE7] to-[#22D3EE]" />
          <p className="text-[11px] font-extrabold uppercase tracking-wide text-[#375AE7]">
            Why this matched
          </p>
          <p className="text-[12.5px] text-slate-700 leading-relaxed mt-1.5">
            {checkIntro(row.key)} {sourceAction(row, label)}
          </p>
          <p className="text-[10.5px] font-semibold text-slate-400 mt-2 flex items-center gap-1.5">
            <ClockIcon size={10} /> Matched on {dateText}
          </p>
        </div>

        {/* source link url */}
        <p className="text-[10.5px] font-extrabold uppercase tracking-wider text-slate-400 mt-4 mb-2">
          Register link
        </p>
        <div className="rounded-xl border border-slate-200 bg-slate-50/80 overflow-hidden">
          <div className="flex items-center gap-2 px-3 py-2.5">
            <svg
              width="13"
              height="13"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              className="text-[#375AE7] shrink-0"
            >
              <rect x="3" y="11" width="18" height="11" rx="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
            <span
              className="flex-1 min-w-0 text-[11.5px] font-mono font-semibold text-slate-600 break-all leading-snug"
              title={href}
            >
              {href}
            </span>
            <button
              onClick={handleCopy}
              aria-label="Copy register link"
              className={`shrink-0 inline-flex items-center gap-1 text-[10.5px] font-bold rounded-full px-2.5 py-1.5 ring-1 transition-all ${
                copied
                  ? 'bg-emerald-50 text-emerald-600 ring-emerald-200'
                  : 'bg-white text-slate-500 ring-slate-200 hover:text-[#375AE7] hover:ring-[#375AE7]/40'
              }`}
            >
              {copied ? (
                <>
                  <svg
                    width="11"
                    height="11"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                  >
                    <path d="M20 6L9 17l-5-5" />
                  </svg>
                  Copied
                </>
              ) : (
                <>
                  <svg
                    width="11"
                    height="11"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  >
                    <rect x="9" y="9" width="13" height="13" rx="2" />
                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                  </svg>
                  Copy
                </>
              )}
            </button>
          </div>
        </div>

        <div className="flex items-start gap-2 mt-3 rounded-xl bg-amber-50/70 ring-1 ring-amber-100 px-3.5 py-2.5">
          <span className="text-amber-500 mt-0.5 shrink-0">
            <ShieldCheckGlyph size={16} />
          </span>
          <p className="text-[11px] text-amber-800/80 leading-relaxed">
            {label} is a third-party register. Verify the matched record
            directly on the source before treating this case.
          </p>
        </div>
      </div>

      {/* footer */}
      <div className="flex items-center justify-between gap-2 px-5 py-3.5 border-t border-slate-100 bg-slate-50/60 shrink-0">
        <button
          onClick={onClose}
          className="inline-flex items-center gap-1.5 h-10 px-3 text-[13px] font-semibold text-slate-500 hover:text-slate-800 transition-colors"
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
          >
            <path d="M19 12H5M11 18l-6-6 6-6" />
          </svg>
          Back
        </button>
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 h-10 px-4 rounded-xl bg-gradient-to-r from-[#375AE7] to-[#5B7CFA] text-white text-[13px] font-bold shadow-[0_12px_24px_-12px_rgba(55,90,231,.85)] hover:-translate-y-0.5 transition-all active:scale-[0.97]"
        >
          Open register
          <ExternalIcon size={12} />
        </a>
      </div>
    </ModalShell>
  );
}

function CloseButton({ onClick }) {
  return (
    <button
      onClick={onClick}
      aria-label="Close"
      className="w-8 h-8 rounded-full flex items-center justify-center text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors"
    >
      <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      >
        <path d="M18 6L6 18M6 6l12 12" />
      </svg>
    </button>
  );
}
